// src/pages/manager/ManagerCoachList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import UserLink from '../../components/common/UserLink';
import UserStatusBadge from '../../components/common/UserStatusBadge';
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from '../../components/layout/ListPageLayout';
import PaginationFooter from '../../components/common/PaginationFooter';
import SortableTableHeader from '../../components/common/SortableTableHeader';
import ListRecordIdBadge from '../../components/common/ListRecordIdBadge';
import useSortedClientPagination from '../../hooks/useSortedClientPagination';
import { userListSortColumns } from '../../config/tableSortColumns';
import { resolveRowId } from '../../utils/tableSort';
import { formatFullName } from '../../utils/name';

function useDebounced(value, delay = 200) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

const ManagerCoachList = () => {
    const { t } = useTranslation();
    const [coaches, setCoaches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [filter, setFilter] = useState('active');
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);

    useEffect(() => {
        setLoadError(false);
        API.get('/manager/users', { params: { role: 'COACH' } })
            .then(res => setCoaches(Array.isArray(res.data) ? res.data : []))
            .catch(err => {
                console.error('Error fetching coaches:', err);
                setCoaches([]);
                setLoadError(true);
            })
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        let result = coaches || [];
        if (filter === 'active') {
            result = result.filter(c => c.active !== false);
        } else if (filter === 'archived') {
            result = result.filter(c => c.active === false);
        }
        const term = (dq || '').trim().toLowerCase();
        if (term) {
            const pick = (x) => (x ?? '').toString().toLowerCase();
            result = result.filter(u =>
                [
                    u.firstName, u.middleName, u.lastName,
                    u.username || u.userName,
                    u.email,
                    u.phoneNumber || u.phone,
                    u.id || u.userId || u.user_id
                ].some(v => pick(v).includes(term))
            );
        }
        return result;
    }, [coaches, dq, filter]);

    const pagination = useSortedClientPagination(filtered, {
        columns: userListSortColumns,
        resetKey: `${filter}-${dq}`,
    });

    const shown = filtered.length;
    const total = filter === 'all' ? coaches.length : filter === 'active' ? coaches.filter(c => c.active !== false).length : coaches.filter(c => c.active === false).length;

    const filterTabs = [
        { key: "all", label: t('common.all'), count: coaches.length },
        { key: "active", label: t('common.active'), count: coaches.filter(c => c.active !== false).length },
        { key: "archived", label: t('common.archived'), count: coaches.filter(c => c.active === false).length },
    ];

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('pages.managerCoaches.title')}
                subtitle={t('pages.managerCoaches.subtitle')}
            />

            <ListFilterTabs tabs={filterTabs} active={filter} onChange={setFilter} />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('pages.managerCoaches.searchPlaceholder')}
                shown={shown}
                total={total}
                entityLabel={t('pages.managerCoaches.entityLabel')}
            />

            {loadError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {t('common.listLoadFailed')}
                </div>
            )}
            {loading ? (
                <ListLoadingSpinner />
            ) : loadError ? null : filtered.length === 0 ? (
                <ListEmptyState
                    message={q ? t('pages.managerCoaches.noMatch') : t('pages.managerCoaches.empty')}
                    hint={q ? t('common.emptySearchHint') : undefined}
                />
            ) : (
                <ListTableCard>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="table-header">
                                <tr>
                                    <SortableTableHeader label={t('common.idColumn')} columnKey="id" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('table.coachName')} columnKey="name" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.username')} columnKey="username" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.email')} columnKey="email" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.phone')} columnKey="phone" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.status')} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pagination.pageItems.map(u => (
                                    <tr key={String(u.id ?? u.userId ?? u.user_id)} className="table-row">
                                        <td className="table-cell whitespace-nowrap">
                                            <ListRecordIdBadge id={resolveRowId(u)} />
                                        </td>
                                        <td className="table-cell">
                                            {formatFullName(u.firstName, u.middleName, u.lastName) ? (
                                                <UserLink
                                                    id={u.id ?? u.userId}
                                                    name={formatFullName(u.firstName, u.middleName, u.lastName)}
                                                />
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="table-cell">{u.username || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">{u.email || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">{u.phoneNumber || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">
                                            <UserStatusBadge active={u.active} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pagination.total > 0 && (
                        <PaginationFooter
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onPageChange={pagination.setPage}
                            onSizeChange={pagination.setSize}
                        />
                    )}
                </ListTableCard>
            )}
        </ListPageLayout>
    );
};

export default ManagerCoachList;
